/** The latest count of a key: its date and the audit snapshot taken when it was saved. */
export type MedicineLastCount = {
    date: string;
    difference: number;
    /** The balance the ledger showed just before the count; the denominator of the deviation percentage. */
    calculated: number;
};

/** The oldest purchase or count the ledger still counts stock from, with how much of it is left. */
export type MedicineActiveLot = {
    source: 'purchase' | 'count';
    date: string;
    quantity: number;
    remaining: number;
};

export type MedicineLotStatus = 'active' | 'waiting' | 'used' | 'reset';

/**
 * One purchase or count the ledger counts stock from, and what became of it: drawn by treatments and
 * spillage (\`used\`), closed by a later count (\`reset\`), or still on the shelf (\`remaining\`).
 * quantity = used + reset + remaining.
 */
export type MedicineLot = {
    source: 'purchase' | 'count';
    /** Id of the purchase or count, which is what opens its form. */
    record_id: number;
    date: string;
    quantity: number;
    used: number;
    reset: number;
    remaining: number;
    status: MedicineLotStatus;
    batch_number: string | null;
    packs: number | null;
    pack_size: number | null;
};

/** One stock ledger key (farm + drug + unit) with its balance reconciled over the selected period. */
export type MedicineInventoryRow = {
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
    opening: number;
    bought: number;
    used: number;
    /** Written off through Spild during the period; part of the balance, not of `used`. */
    wasted: number;
    adjustment: number;
    closing: number;
    last_purchase_date: string | null;
    /** True when a non-deleted purchase exists for the key on or before the end of the period; a purchase dated after it does not count. */
    has_purchase: boolean;
    last_count: MedicineLastCount | null;
    /** Null when no lot has anything left (nothing bought or counted, or all of it used). */
    active_lot: MedicineActiveLot | null;
    /** Every lot of the key, oldest first; only the detail endpoint serves them, so the list's rows carry none. */
    lots: MedicineLot[];
    /** The day the farm's stock starts (its first purchase, count or waste of any drug); treatments before it are not deducted. Detail only; null when not served or not started. */
    stock_start: string | null;
};

export type MedicineMovementType = 'purchase' | 'treatment' | 'retreatment' | 'waste' | 'count';

export type MedicineMovementDetails = {
    packs?: number;
    pack_size?: number;
    batch_number?: string | null;
    note?: string | null;
    location_text?: string | null;
    animals?: number;
    treatment_day?: number | null;
    repetitions?: number | null;
    counted?: number;
    calculated?: number;
    difference?: number;
    reason_id?: number | null;
    reason_name?: string | null;
};

/** One lot a treatment or spillage drew from, and how much of it. */
export type MedicineDrawnFrom = {
    source: 'purchase' | 'count';
    date: string;
    batch_number: string | null;
    quantity: number;
};

/** One chronological ledger entry: a purchase, a count, a spillage or a treatment's derived consumption. */
export type MedicineMovement = {
    key: string;
    date: string;
    type: MedicineMovementType;
    /** Signed: purchases add, treatments and spillage subtract, a count shows its signed difference. */
    quantity: number;
    balance_after: number;
    /** The lots a treatment or spillage drew from, one entry per lot; empty for other entries and outside a single key's movements. */
    drawn_from: MedicineDrawnFrom[];
    /** The part of a treatment or spillage that no lot covered (consumption without a purchase); 0 when fully covered. */
    uncovered: number;
    /** What a purchase covered of earlier uncovered consumption; 0 for everything else. */
    covered_deficit: number;
    /** The diagnosis of the instruction behind the entry; a key with several active instructions joins them with " / ". Null when there is none. */
    diagnosis: string | null;
    user_name: string | null;
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
    /** Id of the purchase, spillage or count; null for treatments. */
    resource_id: number | null;
    /** Id of the treatment's registration; null for purchases, spillage and counts. */
    record_id: number | null;
    editable: boolean;
    deletable: boolean;
    details: MedicineMovementDetails;
};

/** One subtotal of treatment consumption for a diagnosis in one unit, as the endpoint serves it. */
export type MedicineDiagnosisTotalApiRow = {
    diagnosis: string | null;
    dosage_unit_id: number;
    dosage_unit: string;
    /** Positive consumption. */
    quantity: number;
    treatments: number;
    drug_names: string[];
};

/** One page of movements as the endpoint serves it. */
export type MedicineMovementsApiPage = {
    data: MedicineMovementApiRow[];
    page_number: number;
    page_size: number;
    next_page: number | null;
    total_pages: number | null;
    total_rows: number | null;
    /** Only when the request is narrowed to treatments alone: consumption per diagnosis and unit over the whole filtered set, not the page. */
    totals_by_diagnosis?: MedicineDiagnosisTotalApiRow[];
    /** Only when the request names one drug and unit: the signed sum of quantity over the whole filtered set, not the page. */
    net_quantity?: number | null;
};

/** `GET health/inventory/keys`: every farm, drug and unit the ledger could hold entries for, without any figures. */
export type MedicineInventoryKeyApiRow = {
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    dosage_unit_id: number;
    dosage_unit: string;
};

export type MedicineInventoryKey = {
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
};

/** `GET health/inventory` and `GET health/inventory/{farmId}/{drugId}/{unitId}`, verbatim. */
export type MedicineInventoryApiRow = {
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    dosage_unit_id: number;
    dosage_unit: string;
    opening: number;
    bought: number;
    used: number;
    wasted: number;
    adjustment: number;
    closing: number;
    last_purchase_date: string | null;
    last_count: MedicineLastCount | null;
    /** Null when no lot has anything left (nothing bought or counted, or all of it used). */
    active_lot: MedicineActiveLot | null;
    /** Only the detail endpoint carries the lots and the stock start. */
    lots?: MedicineLot[];
    stock_start?: string | null;
};

/** `GET health/inventory/movements` rows, verbatim. */
export type MedicineMovementApiRow = {
    /** Identity within its type only - a purchase and a count can share one. */
    id: number;
    date: string;
    type: MedicineMovementType;
    farm_id: number;
    farm_name: string;
    drug_id: number;
    drug_name: string;
    dosage_unit_id: number;
    dosage_unit: string;
    quantity: number;
    balance_after: number;
    /** The next three come only from a single key's movements request. */
    drawn_from?: MedicineDrawnFrom[] | null;
    uncovered?: number | null;
    covered_deficit?: number | null;
    diagnosis: string | null;
    user: string | null;
    resource_id: number | null;
    record_id: number | null;
    editable: boolean;
    deletable: boolean;
    details: {
        packs: number | null;
        pack_size: number | null;
        batch_number: string | null;
        location: string | null;
        animals: number | null;
        treatment_day: number | null;
        repetitions: number | null;
        counted: number | null;
        calculated: number | null;
        difference: number | null;
        reason: string | null;
        note: string | null;
    };
};

/** The wire names a dosage unit `dosage_unit_id` / `dosage_unit`; the inventory reads `unit_id` / `unit_name`. */
export function unitFields(raw: { dosage_unit_id: number; dosage_unit: string }): { unit_id: number; unit_name: string } {
    return { unit_id: raw.dosage_unit_id, unit_name: raw.dosage_unit };
}

/** `MedicineInventoryApiRow` under the names the inventory tables read. */
export function mapMedicineInventoryRow(raw: MedicineInventoryApiRow): MedicineInventoryRow {
    return {
        farm_id: raw.farm_id,
        farm_name: raw.farm_name,
        drug_id: raw.drug_id,
        drug_name: raw.drug_name,
        ...unitFields(raw),
        opening: raw.opening,
        bought: raw.bought,
        used: raw.used,
        wasted: raw.wasted,
        adjustment: raw.adjustment,
        closing: raw.closing,
        last_purchase_date: raw.last_purchase_date,
        // The wire carries the latest purchase date up to the end of the period, which is null exactly when no purchase had been made by then.
        has_purchase: raw.last_purchase_date !== null,
        last_count: raw.last_count,
        active_lot: raw.active_lot ?? null,
        lots: raw.lots ?? [],
        stock_start: raw.stock_start ?? null,
    };
}

/** `MedicineMovementApiRow` under the names the movements tables read; absent detail fields become undefined. */
export function mapMedicineMovement(raw: MedicineMovementApiRow): MedicineMovement {
    const { details } = raw;

    return {
        key: `${raw.type}-${raw.id}`,
        date: raw.date,
        type: raw.type,
        quantity: raw.quantity,
        balance_after: raw.balance_after,
        drawn_from: raw.drawn_from ?? [],
        uncovered: raw.uncovered ?? 0,
        covered_deficit: raw.covered_deficit ?? 0,
        diagnosis: raw.diagnosis,
        user_name: raw.user,
        farm_id: raw.farm_id,
        farm_name: raw.farm_name,
        drug_id: raw.drug_id,
        drug_name: raw.drug_name,
        ...unitFields(raw),
        resource_id: raw.resource_id,
        record_id: raw.record_id,
        editable: raw.editable,
        deletable: raw.deletable,
        details: {
            packs: details.packs ?? undefined,
            pack_size: details.pack_size ?? undefined,
            batch_number: details.batch_number,
            note: details.note,
            location_text: details.location,
            animals: details.animals ?? undefined,
            treatment_day: details.treatment_day,
            repetitions: details.repetitions,
            counted: details.counted ?? undefined,
            calculated: details.calculated ?? undefined,
            difference: details.difference ?? undefined,
            reason_name: details.reason,
        },
    };
}

/** The period and farm scope every inventory endpoint takes. */
export type MedicineInventoryScope = {
    period: string;
    locationIds: number[];
};

/** The form ids of the inventory writes, as registered in the forms engine. */
/* eslint-disable-next-line no-shadow */
export enum MedicineInventoryFormId {
    Purchase = 48,
    Count = 49,
    Waste = 50,
}

export type MedicineMovementSort = 'date' | 'quantity' | 'user';

/** What one movements request asks the server for: scope, optional key and type filters, sort and page. */
export type MedicineMovementsParams = {
    scope: MedicineInventoryScope;
    /** Only together with unitId: the endpoint narrows to one drug in one unit. */
    drugId?: number;
    unitId?: number;
    /** The Log's drug filter: rows of any of these drugs in their unit, as `drugUnitValue` pairs; empty means no narrowing. Not combined with drugId. */
    drugs?: string[];
    /** The key's whole ledger, whatever the period: set on a detail page, where history is never cut. Needs drugId and unitId. */
    allHistory?: boolean;
    types: MedicineMovementType[];
    /** Rows whose diagnosis is any of these; empty means no narrowing. */
    diagnoses: string[];
    /** Free text the rows' drug, farm, location, diagnosis, user, note or batch number must contain; blank means no narrowing. */
    search?: string;
    sort: MedicineMovementSort;
    descending: boolean;
    page: number;
    pageSize: number;
};

/** One subtotal of treatment consumption for a diagnosis in one unit. */
export type MedicineDiagnosisTotal = {
    diagnosis: string | null;
    unit_id: number;
    unit_name: string;
    quantity: number;
    treatments: number;
    drug_names: string[];
};

export type MedicineMovementsResult = {
    rows: MedicineMovement[];
    totalRows: number;
    totalPages: number;
    /** Present only for a treatments-only request; the endpoint leaves it out otherwise. */
    diagnosisTotals: MedicineDiagnosisTotal[] | null;
    /** Present only when the request named one drug and unit. */
    netQuantity: number | null;
};

/** What the Log's treatments chart asks for: the movements' own farm, drug and diagnosis filters, never a type, page or sort. */
export type MedicineTreatmentWeeksParams = {
    scope: MedicineInventoryScope;
    drugs: string[];
    diagnoses: string[];
};

/** `GET health/inventory/treatments-by-week`: the treatments and retreatments of one drug in one week. Weeks without any are left out. */
export type MedicineTreatmentWeek = {
    /** Monday of the week, "yyyy-MM-dd". */
    week_start: string;
    drug_id: number;
    drug_name: string;
    treatments: number;
    animals: number;
};

/** `GET health/inventory/bulk-count?farm_id=&date=`: the keys a count of the farm covers, with the balance each is expected to show. */
export type MedicineBulkCountApiResponse = {
    farm_id: number;
    date: string;
    rows: { drug_id: number; drug_name: string; dosage_unit_id: number; dosage_unit: string; calculated: number }[];
    reasons: { id: number; name: string }[];
};

/** One row of the bulk count as the user fills it in. */
export type MedicineBulkCountRow = {
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
    calculated: number;
};

export type MedicineBulkCount = {
    farmId: number;
    date: string;
    rows: MedicineBulkCountRow[];
    reasons: { id: number; name: string }[];
};

/** The body of `POST health/inventory/bulk-count`: only the rows that were counted. */
export type MedicineBulkCountPayload = {
    farm_id: number;
    date: string;
    rows: { drug_id: number; dosage_unit_id: number; counted: number; reason_id: number | null; note: string | null }[];
};

/** One row the server refused to save, from a bulk count or a bulk purchase. */
/** `drug_id` and `dosage_unit_id` are null for an error about the farm or the date rather than a row. */
export type MedicineBulkRowError = { drug_id: number | null; dosage_unit_id: number | null; code: string; message: string };

/** What a drug was last bought as on the farm, which a new purchase of it starts from. */
export type MedicineLastPurchase = { date: string; packs: number; pack_size: number };

/** `GET health/inventory/bulk-purchase?farm_id=&date=`: the keys a purchase for the farm offers, each with its last purchase. */
export type MedicineBulkPurchaseApiResponse = {
    farm_id: number;
    date: string;
    rows: { drug_id: number; drug_name: string; dosage_unit_id: number; dosage_unit: string; last_purchase: MedicineLastPurchase | null }[];
};

/** One row of the bulk purchase as the user fills it in. */
export type MedicineBulkPurchaseRow = {
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
    last_purchase: MedicineLastPurchase | null;
};

export type MedicineBulkPurchase = {
    farmId: number;
    date: string;
    rows: MedicineBulkPurchaseRow[];
};

/** The body of `POST health/inventory/bulk-purchase`: only the rows that were ticked. */
export type MedicineBulkPurchasePayload = {
    farm_id: number;
    date: string;
    rows: { drug_id: number; dosage_unit_id: number; packs: number; pack_size: number; batch_number: string | null; note: string | null }[];
};

/** `GET health/inventory/count-sheet`: per farm, the keys with an active instruction and the balance today. */
export type MedicineCountSheetApiFarm = {
    farm_id: number;
    farm_name: string;
    farm_number?: string | null;
    rows: { drug_id: number; drug_name: string; dosage_unit_id: number; dosage_unit: string; calculated: number }[];
};

export type MedicineCountSheetFarm = Omit<MedicineCountSheetApiFarm, 'rows'> & {
    rows: { drug_id: number; drug_name: string; unit_id: number; unit_name: string; calculated: number }[];
};

/** `GET health/inventory/count-dates`: the days a farm has any count on, newest first. */
export type MedicineCountDatesFarm = {
    farm_id: number;
    farm_name: string;
    dates: string[];
};

/** One farm and the two count days the report compares, earlier first. */
export type MedicineReportRequestFarm = { farm_id: number; start_date: string; end_date: string };

/** One drug-and-unit column of a farm's report. Start, end and expected are null when the key was not counted on that day (or nothing could be expected). */
export type MedicineReportApiColumn = {
    drug_id: number;
    drug_name: string;
    dosage_unit_id: number;
    dosage_unit: string;
    start: number | null;
    bought: number;
    used: number;
    wasted: number;
    expected: number | null;
    end: number | null;
    /** end / expected - 1. Not read: the deviation is recomputed from `end` and `expected` so the wire's scale never matters. */
    deviation: number | null;
};

export type MedicineReportApiFarm = {
    farm_id: number;
    farm_name: string;
    start_date: string;
    end_date: string;
    columns: MedicineReportApiColumn[];
};

export type MedicineReportColumn = {
    drug_id: number;
    drug_name: string;
    unit_id: number;
    unit_name: string;
    start: number | null;
    bought: number;
    used: number;
    wasted: number;
    expected: number | null;
    end: number | null;
    /** Percent: end / expected - 1, times 100, as signedDeviationPercent. Null when the key was not counted on both days, or it was off while nothing was expected. */
    deviation_percent: number | null;
};

export type MedicineReportFarm = {
    farm_id: number;
    farm_name: string;
    start_date: string;
    end_date: string;
    columns: MedicineReportColumn[];
};

/**
 * A count's deviation as a signed share of what the ledger expected: difference / expected * 100, the one rule every
 * stock view shows. A count that matched is 0 % whatever was expected; otherwise null when nothing was expected (an
 * expected balance of zero or less has no meaningful share).
 */
export function signedDeviationPercent(difference: number, expected: number): number | null {
    if (difference === 0) {
        return 0;
    }

    return expected > 0 ? (difference / expected) * 100 : null;
}

/** The most farms one report can compare. */
export const MEDICINE_REPORT_MAX_FARMS = 10;

export function mapMedicineReportFarm(raw: MedicineReportApiFarm): MedicineReportFarm {
    return {
        farm_id: raw.farm_id,
        farm_name: raw.farm_name,
        start_date: raw.start_date,
        end_date: raw.end_date,
        columns: raw.columns.map((column) => ({
            drug_id: column.drug_id,
            drug_name: column.drug_name,
            ...unitFields(column),
            start: column.start,
            bought: column.bought,
            used: column.used,
            wasted: column.wasted,
            expected: column.expected,
            end: column.end,
            deviation_percent: column.end !== null && column.expected !== null ? signedDeviationPercent(column.end - column.expected, column.expected) : null,
        })),
    };
}
